import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import StarIcon from "@mui/icons-material/Star";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const API_URL = process.env.REACT_APP_API_URL;

// Dark theme constants (matching admin dashboard)
const dark = {
  bg: "#1a1a2e",
  card: "#16213e",
  border: "#394867",
  text: "#e0e0e0",
  textSecondary: "#9e9e9e",
  textMuted: "#616161",
  input: "#0f3460",
  inputBorder: "#394867",
};

const darkTextField = {
  "& .MuiOutlinedInput-root": {
    color: dark.text,
    backgroundColor: dark.input,
    "& fieldset": { borderColor: dark.inputBorder },
    "&:hover fieldset": { borderColor: "#5a6f8a" },
    "&.Mui-focused fieldset": { borderColor: "#64b5f6" },
  },
  "& .MuiInputLabel-root": { color: dark.textSecondary },
  "& .MuiInputLabel-root.Mui-focused": { color: "#64b5f6" },
};

const darkSelect = {
  color: dark.text,
  backgroundColor: dark.input,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: dark.inputBorder },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#5a6f8a" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#64b5f6" },
  "& .MuiSvgIcon-root": { color: dark.textSecondary },
};

const darkDialog = {
  "& .MuiDialog-paper": {
    backgroundColor: dark.card,
    color: dark.text,
    border: `1px solid ${dark.border}`,
  },
};

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [totalImages, setTotalImages] = useState(0);
  const [totalWithAlt, setTotalWithAlt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [denomFilter, setDenomFilter] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create");
  const [editingPost, setEditingPost] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletePost, setDeletePost] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [imageManagerPost, setImageManagerPost] = useState(null);
  const [postImages, setPostImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editorImages, setEditorImages] = useState([]);
  const [deleteImageKey, setDeleteImageKey] = useState(null);
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  // Cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropFileName, setCropFileName] = useState("");
  const [cropQueue, setCropQueue] = useState([]);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropTargetPost, setCropTargetPost] = useState(null);
  const [cropSource, setCropSource] = useState(null); // "editor" or "manager"
  const [cropCurrentFile, setCropCurrentFile] = useState(null);
  const [cropAspect, setCropAspect] = useState(16 / 9);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [editAltImage, setEditAltImage] = useState(null);
  const [editAltText, setEditAltText] = useState("");
  const [cropMaxWidth, setCropMaxWidth] = useState(1200);
  const [formData, setFormData] = useState({
    title: "", slug: "", description: "", content: "", author: "",
    category: "", category_slug: "", denomination: "", featured_image_url: "",
    status: "draft", featured: false, read_time: "", platform: "malayaleechristians",
  });

  useEffect(() => {
    fetchPosts().then(() => {
      const params = new URLSearchParams(window.location.search);
      const editSlug = params.get("edit");
      if (editSlug) {
        // Will be handled once posts are loaded
        window.__pendingEditSlug = editSlug;
      } else if (params.has("new")) {
        handleCreate();
      }
    });
  }, []);

  // Open editor from URL after posts load
  useEffect(() => {
    if (posts.length > 0 && window.__pendingEditSlug) {
      const post = posts.find((p) => p.slug === window.__pendingEditSlug);
      if (post) handleEdit(post);
      window.__pendingEditSlug = null;
    }
  }, [posts]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const getCroppedBlob = (imageSrc, pixelCrop) => {
    return new Promise((resolve) => {
      const image = new window.Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
        canvas.toBlob((blob) => resolve(blob), "image/png", 1);
      };
    });
  };

  const startCropFlow = (files, targetPost, source) => {
    if (!files || files.length === 0) return;
    setCropTargetPost(targetPost);
    setCropSource(source);
    const queue = Array.from(files).map((f) => ({ file: f, url: URL.createObjectURL(f), name: f.name }));
    setCropQueue(queue.slice(1));
    setCropImage(queue[0].url);
    setCropFileName(queue[0].name);
    setCropCurrentFile(queue[0].file);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropperOpen(true);
  };

  const handleCropConfirm = async () => {
    if (!cropImage || !croppedAreaPixels || !cropTargetPost) return;
    const blob = await getCroppedBlob(cropImage, croppedAreaPixels);
    const formData = new FormData();
    formData.append("images", blob, cropFileName || "cropped.png");
    formData.append("maxWidth", cropMaxWidth.toString());

    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/admin/blog/${cropTargetPost.post_id}/images`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (err) { console.error("Upload failed:", err); }
    finally { setUploading(false); }

    // Process next in queue or close
    if (cropQueue.length > 0) {
      const next = cropQueue[0];
      setCropQueue(cropQueue.slice(1));
      setCropImage(next.url);
      setCropFileName(next.name);
      setCropCurrentFile(next.file);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } else {
      setCropperOpen(false);
      setCropImage(null);
      // Refresh images
      if (cropSource === "editor") {
        try {
          const response = await axios.get(`${API_URL}/api/admin/blog/${cropTargetPost.post_id}/images`);
          setEditorImages(response.data.data?.images || []);
        } catch (err) { /* ignore */ }
      } else if (cropSource === "manager") {
        try {
          const response = await axios.get(`${API_URL}/api/admin/blog/${cropTargetPost.post_id}/images`);
          setPostImages(response.data.data?.images || []);
        } catch (err) { /* ignore */ }
      }
      fetchPosts();
    }
  };

  const handleCropSkip = async () => {
    if (!cropCurrentFile || !cropTargetPost) return;

    // Upload the original file without cropping (still goes through sharp on backend)
    const formData = new FormData();
    formData.append("images", cropCurrentFile, cropFileName || "upload.png");
    formData.append("maxWidth", cropMaxWidth.toString());

    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/admin/blog/${cropTargetPost.post_id}/images`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (err) { console.error("Upload failed:", err); }
    finally { setUploading(false); }

    // Process next in queue or close
    if (cropQueue.length > 0) {
      const next = cropQueue[0];
      setCropQueue(cropQueue.slice(1));
      setCropImage(next.url);
      setCropFileName(next.name);
      setCropCurrentFile(next.file);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } else {
      setCropperOpen(false);
      setCropImage(null);
      // Refresh images
      if (cropSource === "editor") {
        try {
          const response = await axios.get(`${API_URL}/api/admin/blog/${cropTargetPost.post_id}/images`);
          setEditorImages(response.data.data?.images || []);
        } catch (err) { /* ignore */ }
      } else if (cropSource === "manager") {
        try {
          const response = await axios.get(`${API_URL}/api/admin/blog/${cropTargetPost.post_id}/images`);
          setPostImages(response.data.data?.images || []);
        } catch (err) { /* ignore */ }
      }
      fetchPosts();
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/blog`);
      setPosts(response.data.data?.posts || []);
      setTotalImages(response.data.data?.totalImages || 0);
      setTotalWithAlt(response.data.data?.totalWithAlt || 0);
    } catch (err) { console.error("Failed to fetch posts:", err); }
    finally { setLoading(false); }
  };

  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();
  const generateCategorySlug = (cat) => cat.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();

  const handleCreate = () => {
    setEditorMode("create"); setEditingPost(null);
    setFormData({ title: "", slug: "", description: "", content: "", author: "", category: "", category_slug: "", denomination: "", featured_image_url: "", status: "draft", featured: false, read_time: "", platform: "malayaleechristians" });
    setEditorOpen(true);
    window.history.pushState(null, "", "/admin/dashboard/blog?new");
  };

  const handleEdit = async (post) => {
    setEditorMode("edit"); setEditingPost(post);
    setFormData({ title: post.title || "", slug: post.slug || "", description: post.description || "", content: post.content || "", author: post.author || "", category: post.category || "", category_slug: post.category_slug || "", denomination: post.denomination || "", featured_image_url: post.featured_image_url || "", status: post.status || "draft", featured: post.featured || false, read_time: post.read_time || "", platform: post.platform || "malayaleechristians" });
    setEditorOpen(true);
    window.history.pushState(null, "", `/admin/dashboard/blog?edit=${post.slug}`);
    // Fetch images for this post
    try {
      const response = await axios.get(`${API_URL}/api/admin/blog/${post.post_id}/images`);
      setEditorImages(response.data.data?.images || []);
    } catch (err) { setEditorImages([]); }
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorImages([]);
    window.history.pushState(null, "", "/admin/dashboard/blog");
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (editorMode === "create") await axios.post(`${API_URL}/api/admin/blog`, formData);
      else if (editingPost) await axios.patch(`${API_URL}/api/admin/blog/${editingPost.post_id}`, formData);
      closeEditor(); fetchPosts();
    } catch (err) { alert(err.response?.data?.message || "Failed to save post"); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async () => {
    if (!deletePost) return;
    setIsDeleting(true);
    try { await axios.delete(`${API_URL}/api/admin/blog/${deletePost.post_id}`); setPosts(posts.filter((p) => p.post_id !== deletePost.post_id)); setDeletePost(null); }
    catch (err) { console.error("Failed to delete:", err); }
    finally { setIsDeleting(false); }
  };

  const handleToggleStatus = async (post) => {
    const newStatus = post.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API_URL}/api/admin/blog/${post.post_id}`, { status: newStatus });
      setPosts(posts.map((p) => p.post_id === post.post_id ? { ...p, status: newStatus, published_at: newStatus === "published" ? new Date().toISOString() : p.published_at } : p));
    } catch (err) { console.error("Failed to update status:", err); }
  };

  const openImageManager = async (post) => {
    setImageManagerPost(post);
    setImagesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/blog/${post.post_id}/images`);
      setPostImages(response.data.data?.images || []);
    } catch (err) { console.error("Failed to fetch images:", err); setPostImages([]); }
    finally { setImagesLoading(false); }
  };

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !imageManagerPost) return;
    startCropFlow(files, imageManagerPost, "manager");
    e.target.value = "";
  };

  const handleImageDelete = async (key) => {
    if (!imageManagerPost) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blog/${imageManagerPost.post_id}/images`, { data: { key } });
      setPostImages(postImages.filter((img) => img.key !== key));
      fetchPosts();
    } catch (err) { console.error("Failed to delete image:", err); }
  };

  const handleSetFeatured = async (url) => {
    if (!imageManagerPost) return;
    try {
      await axios.patch(`${API_URL}/api/admin/blog/${imageManagerPost.post_id}/images/featured`, { url });
      setPostImages(postImages.map((img) => ({ ...img, isFeatured: img.url === url })));
      fetchPosts();
    } catch (err) { console.error("Failed to set featured:", err); }
  };

  const handleSaveAltText = async () => {
    if (!editAltImage) return;
    try {
      await axios.patch(`${API_URL}/api/admin/media/${editAltImage.media_id}`, { alt_text: editAltText });
      setEditorImages(editorImages.map((img) => img.media_id === editAltImage.media_id ? { ...img, alt_text: editAltText } : img));
      setEditAltImage(null);
    } catch (err) { console.error("Failed to save alt text:", err); }
  };

  const handleEditorImageDelete = async () => {
    if (!deleteImageKey || !editingPost) return;
    setIsDeletingImage(true);
    try {
      await axios.delete(`${API_URL}/api/admin/blog/${editingPost.post_id}/images`, { data: { key: deleteImageKey } });
      setEditorImages(editorImages.filter((img) => img.key !== deleteImageKey));
      setDeleteImageKey(null);
      fetchPosts();
    } catch (err) { console.error("Failed to delete image:", err); }
    finally { setIsDeletingImage(false); }
  };

  const categories = ["all", ...new Set(posts.map((p) => p.category).filter(Boolean))];
  const denominations = ["all", ...new Set(posts.map((p) => p.denomination).filter(Boolean))];

  // Category counts
  const categoryCounts = {};
  posts.forEach((p) => { if (p.category) categoryCounts[p.category] = (categoryCounts[p.category] || 0) + 1; });

  // Denomination counts
  const denomCounts = {};
  posts.forEach((p) => { if (p.denomination) denomCounts[p.denomination] = (denomCounts[p.denomination] || 0) + 1; });

  const filteredPosts = posts.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (platformFilter !== "all" && p.platform !== platformFilter) return false;
    if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
    if (denomFilter !== "all" && p.denomination !== denomFilter) return false;
    return true;
  });

  const formatDate = (date) => date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";

  const statusChip = (status) => {
    const colors = { published: { bg: "#1b5e20", color: "#66bb6a" }, draft: { bg: "#e65100", color: "#ffb74d" }, archived: { bg: "#37474f", color: "#90a4ae" } };
    const c = colors[status] || colors.archived;
    return <Chip label={status} size="small" sx={{ backgroundColor: c.bg + "44", color: c.color, border: `1px solid ${c.bg}88`, textTransform: "capitalize" }} />;
  };

  const platformChip = (platform) => {
    const labels = { malayaleechristians: "MC", malayalam_ai: "MAI", both: "Both" };
    const colors = { malayaleechristians: "#1565c0", malayalam_ai: "#7b1fa2", both: "#00838f" };
    const c = colors[platform] || "#555";
    return <Chip label={labels[platform] || platform} size="small" variant="outlined" sx={{ color: c, borderColor: c + "88" }} />;
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: "#fff" }} /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: "#fff" }}>Blog Posts ({posts.length})</Typography>
          <Box display="flex" gap={2} mt={0.5} flexWrap="wrap">
            <Typography variant="caption" sx={{ color: dark.textSecondary }}>{posts.filter(p => p.status === "published").length} published</Typography>
            <Typography variant="caption" sx={{ color: dark.textSecondary }}>{posts.filter(p => p.status === "draft").length} drafts</Typography>
            <Typography variant="caption" sx={{ color: dark.textSecondary }}>{totalImages} images</Typography>
            <Typography variant="caption" sx={{ color: totalWithAlt === totalImages && totalImages > 0 ? "#66bb6a" : "#ffb74d" }}>{totalWithAlt}/{totalImages} with alt text</Typography>
          </Box>
          {/* Category & Denomination summaries */}
          <Box display="flex" gap={3} mt={1} flexWrap="wrap">
            {Object.keys(categoryCounts).length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: dark.textMuted, fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: 1 }}>By Category</Typography>
                <Box display="flex" gap={0.5} mt={0.3} flexWrap="wrap">
                  {Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                    <Chip key={cat} label={`${cat} (${count})`} size="small" onClick={() => setCategoryFilter(categoryFilter === cat ? "all" : cat)}
                      sx={{ height: 20, fontSize: "0.6rem", cursor: "pointer", backgroundColor: categoryFilter === cat ? "#1565c0" : dark.card, color: categoryFilter === cat ? "#fff" : dark.textSecondary, border: `1px solid ${categoryFilter === cat ? "#1565c0" : dark.border}` }} />
                  ))}
                </Box>
              </Box>
            )}
            {Object.keys(denomCounts).length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: dark.textMuted, fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: 1 }}>By Denomination</Typography>
                <Box display="flex" gap={0.5} mt={0.3} flexWrap="wrap">
                  {Object.entries(denomCounts).sort((a, b) => b[1] - a[1]).map(([denom, count]) => (
                    <Chip key={denom} label={`${denom} (${count})`} size="small" onClick={() => setDenomFilter(denomFilter === denom ? "all" : denom)}
                      sx={{ height: 20, fontSize: "0.6rem", cursor: "pointer", backgroundColor: denomFilter === denom ? "#1565c0" : dark.card, color: denomFilter === denom ? "#fff" : dark.textSecondary, border: `1px solid ${denomFilter === denom ? "#1565c0" : dark.border}` }} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={{ backgroundColor: "#1565c0", "&:hover": { backgroundColor: "#0d47a1" } }}>New Post</Button>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ color: dark.textSecondary }}>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={darkSelect}>
            <MenuItem value="all">All ({posts.length})</MenuItem>
            <MenuItem value="draft">Drafts ({posts.filter((p) => p.status === "draft").length})</MenuItem>
            <MenuItem value="published">Published ({posts.filter((p) => p.status === "published").length})</MenuItem>
            <MenuItem value="archived">Archived ({posts.filter((p) => p.status === "archived").length})</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ color: dark.textSecondary }}>Platform</InputLabel>
          <Select value={platformFilter} label="Platform" onChange={(e) => setPlatformFilter(e.target.value)} sx={darkSelect}>
            <MenuItem value="all">All Platforms</MenuItem>
            <MenuItem value="malayaleechristians">Malayalee Christians</MenuItem>
            <MenuItem value="malayalam_ai">Malayalam.ai</MenuItem>
            <MenuItem value="both">Both</MenuItem>
          </Select>
        </FormControl>

        {categories.length > 1 && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {categories.map((cat) => (
              <Chip key={cat} label={cat === "all" ? "All Categories" : cat} size="small" onClick={() => setCategoryFilter(cat)}
                sx={{ cursor: "pointer", textTransform: "capitalize", backgroundColor: categoryFilter === cat ? "#1565c0" : dark.card, color: categoryFilter === cat ? "#fff" : dark.textSecondary, border: `1px solid ${categoryFilter === cat ? "#1565c0" : dark.border}`, "&:hover": { backgroundColor: categoryFilter === cat ? "#0d47a1" : dark.input } }} />
            ))}
          </Box>
        )}
      </Box>

      {/* Table */}
      {filteredPosts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", backgroundColor: dark.card, border: `1px solid ${dark.border}` }}>
          <Typography sx={{ color: dark.textSecondary }}>{posts.length === 0 ? "No blog posts yet. Create your first one!" : "No posts match the filters."}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: dark.card, border: `1px solid ${dark.border}`, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: dark.bg }}>
                {["Title", "Author", "Category", "Denomination", "Platform", "Status", "Featured", "Images", "Created", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ color: dark.textSecondary, fontWeight: 600, borderBottom: `1px solid ${dark.border}`, fontSize: "0.75rem", textTransform: "uppercase" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPosts.map((post) => (
                <TableRow key={post.post_id} sx={{ "&:hover": { backgroundColor: dark.input + "44" }, "& td": { borderBottom: `1px solid ${dark.border}` } }}>
                  <TableCell>
                    <a href={`${process.env.REACT_APP_MC_URL}/article/${post.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: "#fff", fontWeight: 500, fontSize: "0.875rem", textDecoration: "none" }} onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"} onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}>{post.title}</a>
                    <Typography variant="caption" sx={{ color: dark.textMuted }}>/{post.slug}</Typography>
                  </TableCell>
                  <TableCell sx={{ color: dark.textSecondary }}>{post.author}</TableCell>
                  <TableCell sx={{ color: dark.textSecondary }}>{post.category || "-"}</TableCell>
                  <TableCell sx={{ color: dark.textSecondary, fontSize: "0.8rem" }}>{post.denomination || "-"}</TableCell>
                  <TableCell>{platformChip(post.platform)}</TableCell>
                  <TableCell>{statusChip(post.status)}</TableCell>
                  <TableCell>{post.featured && <Chip label="Featured" size="small" sx={{ backgroundColor: "#e65100" + "33", color: "#ffb74d", border: "1px solid #e6510066" }} />}</TableCell>
                  <TableCell sx={{ color: post.image_count > 0 ? "#66bb6a" : dark.textMuted, fontSize: "0.85rem" }}>{post.image_count || 0}</TableCell>
                  <TableCell sx={{ color: dark.textSecondary, fontSize: "0.8rem" }}>{formatDate(post.created_at)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <IconButton size="small" onClick={() => setPreviewPost(post)} sx={{ color: dark.textSecondary, "&:hover": { color: "#fff" } }}><VisibilityIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleEdit(post)} sx={{ color: "#64b5f6", "&:hover": { color: "#90caf9" } }}><EditIcon fontSize="small" /></IconButton>
                      <Button size="small" onClick={() => handleToggleStatus(post)} sx={{ color: post.status === "published" ? "#ffb74d" : "#66bb6a", fontSize: "0.7rem", minWidth: "auto", textTransform: "none" }}>
                        {post.status === "published" ? "Unpublish" : "Publish"}
                      </Button>
                      <IconButton size="small" onClick={() => setDeletePost(post)} sx={{ color: "#ef5350", "&:hover": { color: "#f44336" } }}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onClose={() => closeEditor()} maxWidth="lg" fullWidth sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}` }}>{editorMode === "create" ? "Create New Post" : "Edit Post"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <Box display="flex" gap={2}>
              <TextField label="Title *" value={formData.title} onChange={(e) => { const title = e.target.value; setFormData((prev) => ({ ...prev, title, slug: editorMode === "create" ? generateSlug(title) : prev.slug })); }} fullWidth size="small" sx={darkTextField} />
              <TextField label="Slug *" value={formData.slug} onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))} fullWidth size="small" sx={darkTextField} />
            </Box>
            <Box display="flex" gap={2}>
              <TextField label="Author *" value={formData.author} onChange={(e) => setFormData((prev) => ({ ...prev, author: e.target.value }))} fullWidth size="small" sx={darkTextField} />
              <TextField label="Category" value={formData.category} onChange={(e) => { const category = e.target.value; setFormData((prev) => ({ ...prev, category, category_slug: generateCategorySlug(category) })); }} fullWidth size="small" sx={darkTextField} />
              <TextField label="Category Slug" value={formData.category_slug} onChange={(e) => setFormData((prev) => ({ ...prev, category_slug: e.target.value }))} fullWidth size="small" sx={darkTextField} />
              <TextField label="Read Time" value={formData.read_time} onChange={(e) => setFormData((prev) => ({ ...prev, read_time: e.target.value }))} placeholder="e.g. 5 min read" fullWidth size="small" sx={darkTextField} />
            </Box>
            {/* Denomination */}
            <Box display="flex" gap={2} alignItems="center">
              {formData._denomCustom ? (
                <>
                  <TextField label="Custom Denomination" value={formData.denomination} onChange={(e) => setFormData((prev) => ({ ...prev, denomination: e.target.value }))} size="small" sx={{ ...darkTextField, minWidth: 220 }} placeholder="e.g. Syro-Malankara Catholic" />
                  <Button size="small" onClick={() => setFormData((prev) => ({ ...prev, _denomCustom: false, denomination: "" }))} sx={{ color: dark.textSecondary, fontSize: "0.7rem", textTransform: "none", whiteSpace: "nowrap" }}>Use preset</Button>
                </>
              ) : (
                <>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel sx={{ color: dark.textSecondary }}>Denomination</InputLabel>
                    <Select value={formData.denomination} label="Denomination" onChange={(e) => {
                      if (e.target.value === "__custom__") { setFormData((prev) => ({ ...prev, _denomCustom: true, denomination: "" })); return; }
                      setFormData((prev) => ({ ...prev, denomination: e.target.value }));
                    }} sx={darkSelect}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Syro-Malabar Catholic">Syro-Malabar Catholic</MenuItem>
                      <MenuItem value="Malankara Orthodox">Malankara Orthodox</MenuItem>
                      <MenuItem value="Jacobite Syrian">Jacobite Syrian</MenuItem>
                      <MenuItem value="Mar Thoma">Mar Thoma</MenuItem>
                      <MenuItem value="CSI">CSI</MenuItem>
                      <MenuItem value="Pentecostal (IPC)">Pentecostal (IPC)</MenuItem>
                      <MenuItem value="Latin Catholic">Latin Catholic</MenuItem>
                      <MenuItem value="Knanaya">Knanaya</MenuItem>
                      <MenuItem value="Brethren">Brethren</MenuItem>
                      <MenuItem value="All / General">All / General</MenuItem>
                      <MenuItem value="__custom__"><em>Custom...</em></MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
            <TextField label="Description" value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} multiline rows={2} fullWidth size="small" sx={darkTextField} />
            <TextField label="Featured Image URL" value={formData.featured_image_url} onChange={(e) => setFormData((prev) => ({ ...prev, featured_image_url: e.target.value }))} fullWidth size="small" sx={darkTextField} />
            <TextField label="Content * (Markdown)" value={formData.content} onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))} multiline rows={16} fullWidth size="small" sx={{ ...darkTextField, "& .MuiOutlinedInput-root": { ...darkTextField["& .MuiOutlinedInput-root"], fontFamily: "monospace", fontSize: "0.8rem" } }} />
            <Box display="flex" gap={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: dark.textSecondary }}>Status</InputLabel>
                <Select value={formData.status} label="Status" onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))} sx={darkSelect}>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ color: dark.textSecondary }}>Platform</InputLabel>
                <Select value={formData.platform} label="Platform" onChange={(e) => setFormData((prev) => ({ ...prev, platform: e.target.value }))} sx={darkSelect}>
                  <MenuItem value="malayaleechristians">Malayalee Christians</MenuItem>
                  <MenuItem value="malayalam_ai">Malayalam.ai</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel control={<Checkbox checked={formData.featured} onChange={(e) => setFormData((prev) => ({ ...prev, featured: e.target.checked }))} sx={{ color: dark.textSecondary, "&.Mui-checked": { color: "#64b5f6" } }} />} label={<Typography sx={{ color: dark.text }}>Featured Post</Typography>} />
            </Box>

            {/* Images (only in edit mode) */}
            {editorMode === "edit" && editingPost && (
              <Box sx={{ borderTop: `1px solid ${dark.border}`, pt: 2, mt: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="subtitle2" sx={{ color: dark.textSecondary, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1 }}>Post Images</Typography>
                  <Button size="small" startIcon={<ImageIcon sx={{ fontSize: 16 }} />} onClick={() => openImageManager(editingPost)} sx={{ color: "#66bb6a", fontSize: "0.7rem", textTransform: "none", "&:hover": { backgroundColor: "#66bb6a22" } }}>
                    Manage Images
                  </Button>
                </Box>
                {editorImages.length === 0 ? (
                  <Typography variant="caption" sx={{ color: dark.textMuted }}>No images uploaded. Use the image button (green) in the post list to manage images.</Typography>
                ) : (
                  <Box display="flex" gap={1.5} flexWrap="wrap">
                    {editorImages.map((img) => {
                      const ext = img.key?.split(".").pop()?.toUpperCase() || "?";
                      const sizeKB = img.size ? (img.size / 1024).toFixed(0) : "?";
                      return (
                        <Box key={img.key} sx={{ width: 140, border: `1px solid ${img.isFeatured ? "#66bb6a" : dark.border}`, borderRadius: 1, overflow: "hidden", position: "relative", backgroundColor: dark.bg }}>
                          <Box sx={{ aspectRatio: "16/10", overflow: "hidden", cursor: "pointer" }} onClick={() => { setEditAltImage(img); setEditAltText(img.alt_text || ""); }}>
                            <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          </Box>
                          <Box sx={{ px: 0.8, py: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box>
                              <Typography sx={{ fontSize: "0.6rem", color: dark.textMuted, lineHeight: 1.3 }}>{sizeKB} KB</Typography>
                              <Typography sx={{ fontSize: "0.55rem", color: dark.textMuted, lineHeight: 1.3 }}>{ext}</Typography>
                              {img.alt_text && <Typography sx={{ fontSize: "0.5rem", color: "#64b5f6", lineHeight: 1.2, maxWidth: 60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={img.alt_text}>alt: {img.alt_text}</Typography>}
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.3}>
                              {img.isFeatured && (
                                <Chip label="Featured" size="small" sx={{ height: 16, fontSize: "0.55rem", backgroundColor: "#1b5e20cc", color: "#66bb6a" }} />
                              )}
                              <IconButton size="small" onClick={() => { setEditAltImage(img); setEditAltText(img.alt_text || ""); }} sx={{ color: dark.textMuted, "&:hover": { color: "#ffb74d" }, p: 0.3 }} title="Edit alt text">
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <IconButton size="small" onClick={(e) => {
                                navigator.clipboard.writeText(`![${img.alt_text || "image"}](${img.url})`);
                                const btn = e.currentTarget;
                                const orig = btn.style.color;
                                btn.style.color = "#66bb6a";
                                const tip = document.createElement("span");
                                tip.textContent = "Copied!";
                                tip.style.cssText = "position:absolute;top:-20px;left:50%;transform:translateX(-50%);font-size:10px;color:#66bb6a;white-space:nowrap;pointer-events:none;";
                                btn.style.position = "relative";
                                btn.appendChild(tip);
                                setTimeout(() => { btn.style.color = orig; tip.remove(); }, 1200);
                              }} sx={{ color: dark.textMuted, "&:hover": { color: "#64b5f6" }, p: 0.3 }} title="Copy Markdown">
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <IconButton size="small" onClick={() => setDeleteImageKey(img.key)} sx={{ color: dark.textMuted, "&:hover": { color: "#ef5350" }, p: 0.3 }} title="Delete">
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, p: 2 }}>
          <Button onClick={() => closeEditor()} disabled={isSaving} sx={{ color: dark.textSecondary }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving || !formData.title || !formData.slug || !formData.content || !formData.author} sx={{ backgroundColor: "#1565c0", "&:hover": { backgroundColor: "#0d47a1" } }}>
            {isSaving ? "Saving..." : editorMode === "create" ? "Create Post" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deletePost} onClose={() => setDeletePost(null)} sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}` }}>Delete Blog Post</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ color: dark.text }}>Are you sure you want to delete <strong style={{ color: "#fff" }}>"{deletePost?.title}"</strong>? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, p: 2 }}>
          <Button onClick={() => setDeletePost(null)} disabled={isDeleting} sx={{ color: dark.textSecondary }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}>{isDeleting ? "Deleting..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewPost} onClose={() => setPreviewPost(null)} maxWidth="md" fullWidth sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Preview</span>
          <IconButton onClick={() => setPreviewPost(null)} size="small" sx={{ color: dark.textSecondary }}>✕</IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: dark.border }}>
          {previewPost && (
            <Box>
              <Box display="flex" gap={1} mb={2}>
                {previewPost.category && <Chip label={previewPost.category} size="small" sx={{ backgroundColor: dark.input, color: dark.text, border: `1px solid ${dark.border}` }} />}
                {previewPost.read_time && <Typography variant="caption" sx={{ color: dark.textSecondary, alignSelf: "center" }}>{previewPost.read_time}</Typography>}
                {platformChip(previewPost.platform)}
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: "#fff", mb: 1 }}>{previewPost.title}</Typography>
              {previewPost.description && <Typography variant="body1" sx={{ color: dark.textSecondary, mb: 2 }}>{previewPost.description}</Typography>}
              <Box display="flex" gap={2} mb={3}>
                <Typography variant="body2" sx={{ color: dark.textSecondary }}>{previewPost.author}</Typography>
                <Typography variant="body2" sx={{ color: dark.textMuted }}>{formatDate(previewPost.published_at || previewPost.created_at)}</Typography>
              </Box>
              <Box sx={{ color: dark.text, whiteSpace: "pre-wrap", fontFamily: "inherit", lineHeight: 1.7, "& h2": { fontSize: "1.5rem", fontWeight: "bold", mt: 4, mb: 2, color: "#fff" }, "& h3": { fontSize: "1.25rem", fontWeight: "bold", mt: 3, mb: 1, color: "#fff" }, "& a": { color: "#64b5f6" } }}>
                {previewPost.content}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Manager Dialog */}
      <Dialog open={!!imageManagerPost} onClose={() => setImageManagerPost(null)} maxWidth="md" fullWidth sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <span>Images</span>
            {imageManagerPost && <Typography variant="caption" sx={{ display: "block", color: dark.textSecondary }}>{imageManagerPost.title}</Typography>}
          </Box>
          <IconButton onClick={() => setImageManagerPost(null)} size="small" sx={{ color: dark.textSecondary }}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {/* Upload Area */}
          <Box sx={{ border: `2px dashed ${dark.border}`, borderRadius: 2, p: 4, textAlign: "center", mb: 3, cursor: "pointer", "&:hover": { borderColor: "#64b5f6" }, position: "relative" }}>
            <input type="file" multiple accept="image/*" onChange={handleImageUpload} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} disabled={uploading} />
            {uploading ? (
              <CircularProgress size={24} sx={{ color: "#64b5f6" }} />
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 40, color: dark.textSecondary, mb: 1 }} />
                <Typography sx={{ color: dark.textSecondary }}>Drop images here or click to upload</Typography>
                <Typography variant="caption" sx={{ color: dark.textMuted }}>Max 10MB per file. Automatically converted to WebP.</Typography>
              </>
            )}
          </Box>

          {/* Images Grid */}
          {imagesLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress sx={{ color: "#fff" }} /></Box>
          ) : postImages.length === 0 ? (
            <Typography sx={{ color: dark.textSecondary, textAlign: "center", py: 4 }}>No images uploaded yet.</Typography>
          ) : (
            <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))" gap={2}>
              {postImages.map((img) => (
                <Box key={img.key} sx={{ border: `1px solid ${img.isFeatured ? "#66bb6a" : dark.border}`, borderRadius: 2, overflow: "hidden", position: "relative", backgroundColor: dark.bg }}>
                  <Box sx={{ aspectRatio: "16/10", overflow: "hidden" }}>
                    <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </Box>
                  {img.isFeatured && (
                    <Chip label="Featured" size="small" icon={<StarIcon sx={{ fontSize: 14 }} />} sx={{ position: "absolute", top: 6, left: 6, backgroundColor: "#1b5e20cc", color: "#66bb6a", fontSize: "0.65rem" }} />
                  )}
                  <Box display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.5}>
                    <Typography variant="caption" sx={{ color: dark.textMuted, fontSize: "0.65rem" }}>
                      {img.size ? `${(img.size / 1024).toFixed(0)}KB` : ""}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      {!img.isFeatured && (
                        <IconButton size="small" onClick={() => handleSetFeatured(img.url)} sx={{ color: dark.textSecondary, "&:hover": { color: "#66bb6a" } }} title="Set as featured">
                          <StarIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => handleImageDelete(img.key)} sx={{ color: dark.textSecondary, "&:hover": { color: "#ef5350" } }} title="Delete">
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Alt Text Dialog */}
      <Dialog open={!!editAltImage} onClose={() => setEditAltImage(null)} maxWidth="sm" fullWidth sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}` }}>Edit Alt Text</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editAltImage && (
            <Box>
              <Box sx={{ mb: 2, textAlign: "center", cursor: "pointer" }} onClick={() => { setLightboxImage(editAltImage); }}>
                <img src={editAltImage.url} alt="" style={{ maxHeight: 150, maxWidth: "100%", objectFit: "contain", borderRadius: 4, transition: "opacity 0.15s" }} onMouseOver={(e) => e.currentTarget.style.opacity = "0.8"} onMouseOut={(e) => e.currentTarget.style.opacity = "1"} />
                <Typography variant="caption" sx={{ color: dark.textMuted, fontSize: "0.65rem", mt: 0.5, display: "block" }}>Click image to expand</Typography>
              </Box>
              <TextField
                label="Alt Text"
                value={editAltText}
                onChange={(e) => setEditAltText(e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Describe this image for accessibility and SEO"
                sx={darkTextField}
              />
              <Typography variant="caption" sx={{ color: dark.textMuted, mt: 1, display: "block" }}>
                Used in markdown as ![alt text](url) and for screen readers.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, p: 2 }}>
          <Button onClick={() => setEditAltImage(null)} sx={{ color: dark.textSecondary }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAltText} sx={{ backgroundColor: "#1565c0", "&:hover": { backgroundColor: "#0d47a1" } }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Image Confirmation Dialog */}
      <Dialog open={!!deleteImageKey} onClose={() => setDeleteImageKey(null)} sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}` }}>Delete Image</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ color: dark.text }}>Are you sure you want to delete this image? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, p: 2 }}>
          <Button onClick={() => setDeleteImageKey(null)} disabled={isDeletingImage} sx={{ color: dark.textSecondary }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleEditorImageDelete} disabled={isDeletingImage}>
            {isDeletingImage ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox */}
      {lightboxImage && (
        <Box
          onClick={() => setLightboxImage(null)}
          sx={{
            position: "fixed", inset: 0, zIndex: 9999, backgroundColor: "rgba(0,0,0,0.92)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <img
            src={lightboxImage.url}
            alt=""
            onClick={(e) => e.stopPropagation()}
            onLoad={(e) => {
              const img = e.target;
              const dims = document.getElementById("lightbox-dims");
              if (dims) dims.textContent = `${img.naturalWidth} x ${img.naturalHeight}`;
            }}
            style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", cursor: "default", borderRadius: 4 }}
          />
          <Box sx={{ mt: 2, display: "flex", gap: 3, alignItems: "center" }}>
            <Typography id="lightbox-dims" sx={{ color: "#fff", fontSize: "0.85rem", fontFamily: "monospace" }}>Loading...</Typography>
            <Typography sx={{ color: dark.textSecondary, fontSize: "0.8rem" }}>{lightboxImage.size ? `${(lightboxImage.size / 1024).toFixed(0)} KB` : ""}</Typography>
            <Typography sx={{ color: dark.textSecondary, fontSize: "0.8rem" }}>{lightboxImage.key?.split(".").pop()?.toUpperCase()}</Typography>
            {lightboxImage.isFeatured && <Chip label="Featured" size="small" sx={{ height: 20, fontSize: "0.65rem", backgroundColor: "#1b5e20cc", color: "#66bb6a" }} />}
          </Box>
          <Typography sx={{ color: dark.textMuted, fontSize: "0.7rem", mt: 1 }}>Click anywhere to close</Typography>
        </Box>
      )}

      {/* Cropper Dialog */}
      <Dialog open={cropperOpen} onClose={() => { setCropperOpen(false); setCropImage(null); }} maxWidth="md" fullWidth sx={darkDialog}>
        <DialogTitle sx={{ borderBottom: `1px solid ${dark.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <span>Crop Image</span>
            <Typography variant="caption" sx={{ display: "block", color: dark.textSecondary }}>{cropFileName}{cropQueue.length > 0 ? ` (+${cropQueue.length} more)` : ""}</Typography>
          </Box>
          <IconButton onClick={() => { setCropperOpen(false); setCropImage(null); }} size="small" sx={{ color: dark.textSecondary }}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: "relative", height: 450 }}>
          {cropImage && (
            <Cropper
              image={cropImage}
              crop={crop}
              zoom={zoom}
              aspect={cropAspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              style={{
                containerStyle: { backgroundColor: dark.bg },
                cropAreaStyle: { border: "2px solid #64b5f6" },
              }}
            />
          )}
        </DialogContent>
        <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${dark.border}`, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: dark.textSecondary, minWidth: 40 }}>Zoom</Typography>
          <Slider value={zoom} min={1} max={3} step={0.1} onChange={(e, val) => setZoom(val)} sx={{ color: "#64b5f6", flex: 1, minWidth: 120 }} />
          <Box display="flex" gap={0.5} ml={1}>
            {[
              { label: "16:9", value: 16 / 9 },
              { label: "4:3", value: 4 / 3 },
              { label: "1:1", value: 1 },
              { label: "9:16", value: 9 / 16 },
              { label: "3:2", value: 3 / 2 },
            ].map((opt) => (
              <Chip
                key={opt.label}
                label={opt.label}
                size="small"
                onClick={() => setCropAspect(opt.value)}
                sx={{
                  cursor: "pointer",
                  fontSize: "0.65rem",
                  height: 24,
                  backgroundColor: cropAspect === opt.value ? "#1565c0" : dark.input,
                  color: cropAspect === opt.value ? "#fff" : dark.textSecondary,
                  border: `1px solid ${cropAspect === opt.value ? "#1565c0" : dark.border}`,
                  "&:hover": { backgroundColor: cropAspect === opt.value ? "#0d47a1" : dark.card },
                }}
              />
            ))}
          </Box>
          <Box display="flex" alignItems="center" gap={1} ml={2}>
            <Typography variant="caption" sx={{ color: dark.textSecondary, whiteSpace: "nowrap" }}>Max W</Typography>
            {[800, 1200, 1600, 2400].map((w) => (
              <Chip
                key={w}
                label={`${w}px`}
                size="small"
                onClick={() => setCropMaxWidth(w)}
                sx={{
                  cursor: "pointer", fontSize: "0.6rem", height: 22,
                  backgroundColor: cropMaxWidth === w ? "#1565c0" : dark.input,
                  color: cropMaxWidth === w ? "#fff" : dark.textSecondary,
                  border: `1px solid ${cropMaxWidth === w ? "#1565c0" : dark.border}`,
                  "&:hover": { backgroundColor: cropMaxWidth === w ? "#0d47a1" : dark.card },
                }}
              />
            ))}
          </Box>
        </Box>
        <DialogActions sx={{ borderTop: `1px solid ${dark.border}`, p: 2 }}>
          <Button onClick={handleCropSkip} disabled={uploading} sx={{ color: dark.textSecondary }}>
            {uploading ? "Uploading..." : "Skip Crop & Upload"}
          </Button>
          <Button variant="contained" onClick={handleCropConfirm} disabled={uploading} sx={{ backgroundColor: "#1565c0", "&:hover": { backgroundColor: "#0d47a1" } }}>
            {uploading ? "Uploading..." : "Crop & Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogSection;
